import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Divider,
} from "@mui/material";
import defaultProductImg from "../../../../img/default-image_420.png";
import AddressForm from "../../shared/addressForm";
import { AddressDto } from "@/app/interfaces/AddressDto";
import { ReturnOrderItem } from "@/app/interfaces/ReturnOrderItem";
import dataService from "@/app/services/dataService";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getNextBusinessDay,
  getRenderDayWithTooltip,
  HOURS_LIMIT,
  TimeOptions,
} from "@/app/pages/checkout/pickupDelivery";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import QuickReturnDialog from "./QuickReturnDialog";
import QuickReturnPayload from "@/app/interfaces/payload/QuickReturnPaylaod";
import ThankYouDialog from "./ThankYouDialog";
import { useGlobalNotification } from "../../header/GlobalNotificationContext";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import { useMutation } from "@tanstack/react-query";
import PageLoadingOverlay from "../../shared/pageLoadingOverlay";

function formatDeliveryAddress(address: AddressDto): string {
  if (!address) return "";

  const fullName = `${address.firstName || ""} ${
    address.lastName || ""
  }`.trim();
  const line1 = address.address1 || "";

  const line2Parts = [
    address.city,
    address.stateProvinceName,
    address.zipPostalCode,
  ].filter(Boolean);
  const line2 = line2Parts.join(", ");

  const line3Parts = [
    address.county ? `${address.county}` : null,
    address.countryName,
  ].filter(Boolean);
  const line3 = line3Parts.join(", ");

  return `${fullName}\n${line1}\n${line2}\n${line3}`;
}

const ReturnItemsDetails: React.FC = () => {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const orderId = params.get("orderId");

  const [items, setItems] = useState<ReturnOrderItem[]>([]);

  // per-item pallet flag
  const [itemPalletFlags, setItemPalletFlags] = useState<
    Record<number, "yes" | "no" | "">
  >({});

  const [newShippingAddress, setNewShippingAddress] = useState<AddressDto>({
    id: 0,
    company: "",
    firstName: "",
    lastName: "",
    email: "",
    address1: "",
    address2: "",
    zipPostalCode: "",
    city: "",
    county: "",
    phoneNumber: "",
    countryId: 0,
    stateProvinceId: 0,
    countryName: "",
    stateProvinceName: "",
    availableStates: [],
  });
  const [holidays, setHolidays] = useState<string[]>([]);
  const [quickReturnOpen, setQuickReturnOpen] = useState<boolean>(false);
  const [thankYouOpen, setThankYouOpen] = useState<boolean>(false);
  const [isPageLoading, setPageLoading] = useState<boolean>(true);

  const holidaySet = useMemo(
    () =>
      new Set(
        (holidays ?? [])
          .map((h) => dayjs(h, "YYYY-MM-DD", true))
          .filter((d) => d.isValid())
          .map((d) => d.format("YYYY-MM-DD"))
      ),
    [holidays]
  );

  const minPickupDate = useMemo(() => getNextBusinessDay(holidays), []);
  const [pickupDate, setPickupDate] = useState(minPickupDate);
  const [pickupTime, setPickupTime] = useState<string>(TimeOptions[0].value);
  const [disabledPickupTimes, setDisabledPickupTimes] = useState<Set<string>>(
    new Set()
  );
  const [pickupDatePickerOpen, setPickupDatePickerOpen] = useState(false);

  const [sameLocation, setSameLocation] = useState(true);
  const [jobName, setJobName] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryAddressId, setDeliveryAddressId] = useState(0);

  const { setNotification } = useGlobalNotification();
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  useEffect(() => {
    fetchReturnOrderItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchReturnOrderItems = async () => {
    if (!orderId) {
      setPageLoading(false);
      return;
    }

    try {
      const response = await dataService.fetchReturnOrderItemsDetails(orderId);
      if (response && response.success && response.data) {
        const fetchedItems: ReturnOrderItem[] = response.data.items;
        setItems(fetchedItems);
        setJobName(response.data.jobName);

        // merge backend shipping address into initial shape
        setNewShippingAddress((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(response.data.shippingNewAddress ?? {}).filter(
              ([_, v]) => v !== null && v !== undefined
            )
          ),
        }));

        setDeliveryAddress(
          formatDeliveryAddress(response.data.deliveryAddress)
        );
        setDeliveryAddressId(response.data.deliveryAddress.id ?? 0);
        setHolidays(
          (response.data.publicHolidays ?? [])
            .map((h: string) => dayjs(h, "YYYY-MM-DD", true))
            .filter((d: Dayjs) => d.isValid())
            .map((d: Dayjs) => d.format("YYYY-MM-DD"))
        );

        // Initialize per-item pallet state from backend if available
        setItemPalletFlags(
          fetchedItems.reduce((acc, it) => {
            if (it.palletFlag === true) acc[it.id] = "yes";
            else if (it.palletFlag === false) acc[it.id] = "no";
            else acc[it.id] = "";
            return acc;
          }, {} as Record<number, "yes" | "no" | "">)
        );
      }
    } catch (error) {
      // log / notify if needed
      console.error(error);
      setNotification(
        "Failed to load return items.",
        NotificationTypeEnum.ERROR
      );
    } finally {
      setPageLoading(false);
    }
  };

  const handlePickupDateChange = (newValue: Dayjs | null) => {
    if (!newValue) {
      setDisabledPickupTimes(new Set());
      setPickupTime(TimeOptions[0].value);
      return;
    }
    setPickupDate(newValue);
    handlePickupTime(newValue);
  };

  function handlePickupTime(newValue: Dayjs | null) {
    if (!newValue) {
      setPickupTime(TimeOptions[0].value);
      return;
    }
    const now = dayjs();
    const today = now.startOf("day");
    const tomorrow = today.add(1, "day");

    const disabled = new Set<string>();

    if (newValue.isAfter(tomorrow, "day")) {
      // nothing disabled
    } else if (now.hour() >= HOURS_LIMIT) {
      disabled.add(TimeOptions[0].value);
      disabled.add(TimeOptions[2].value);
    } else if (newValue.isSame(today, "day")) {
      disabled.add(TimeOptions[0].value);
      disabled.add(TimeOptions[2].value);
    }

    setDisabledPickupTimes(disabled);

    const firstEnabled = TimeOptions.find((t) => !disabled.has(t.value));
    setPickupTime(firstEnabled?.value ?? TimeOptions[0].value);
  }

  const shouldDisablePickupDate = useCallback(
    (day: Dayjs) => {
      const isWeekend = day.day() === 0 || day.day() === 6;
      const isHoliday = holidaySet.has(day.format("YYYY-MM-DD"));
      const beforeMin = day.isBefore(minPickupDate, "day");
      return isWeekend || isHoliday || beforeMin;
    },
    [holidaySet, minPickupDate]
  );

  const handleItemNotesChange = (id: number, newNotes: string) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, notes: newNotes } : item))
    );
  };

  const buildItemPayload = (
    item: ReturnOrderItem,
    opts?: {
      newQty?: number;
      newNote?: string;
      newPalletFlag?: "yes" | "no";
    }
  ) => {
    const palletFlagValue =
      opts?.newPalletFlag ?? itemPalletFlags[item.id] ?? "";
    const noteValue = opts?.newNote ?? item.notes ?? "";

    return {
      quantity: opts?.newQty ?? item.quantity,
      note: noteValue,
      palletFlag: palletFlagValue === "yes",
    };
  };

  const syncItemToServer = async (
    itemId: number,
    opts?: { newQty?: number; newNote?: string; newPalletFlag?: "yes" | "no" }
  ) => {
    if (!orderId) return;

    const item = items.find((i) => i.id === itemId);
    if (!item) return;

    const payload = buildItemPayload(item, opts);
    console.log("Sending payload", payload);

    try {
      await dataService.changeReturnItem(Number(orderId), itemId, payload);
    } catch (err) {
      console.error("Failed to sync item", err);
      setNotification(
        "Could not save your changes.",
        NotificationTypeEnum.ERROR
      );
    }
  };

  const handleQtyChange = async (id: number, newQty: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, quantity: newQty } : item
      )
    );

    await syncItemToServer(id, { newQty });
  };

  const handleSubmit = useMutation({
    mutationFn: async () => {
      if (!orderId || !newShippingAddress) return;

      if (!sameLocation) {
        if (!newShippingAddress.address1) {
          setNotification(
            "Please fill out all required fields.",
            NotificationTypeEnum.ERROR
          );
          return;
        }
        if (!newShippingAddress.city) {
          setNotification(
            "Please fill out all required fields.",
            NotificationTypeEnum.ERROR
          );
          return;
        }
        if (!newShippingAddress.zipPostalCode) {
          setNotification(
            "Please fill out all required fields.",
            NotificationTypeEnum.ERROR
          );
          return;
        }
        if (!newShippingAddress.phoneNumber) {
          setNotification(
            "Please fill out all required fields.",
            NotificationTypeEnum.ERROR
          );
          return;
        }
      }
      const response = await dataService.sendOrderAdvanceReturn(orderId, {
        jobNo: jobName,
        newPickupAddress: newShippingAddress,
        pickupDate: pickupDate.toISOString(),
        pickupTime,
        pickupAddressId: deliveryAddressId,
      });
      if (response) {
        if (response.success && response.data) {
          navigate(
            `/order/ReturnItemsConfirmation?rmOrderNumber=${response.data.orderNumber}`
          );
        } else {
          setNotification(
            response.error ?? "Something went wrong. Please try again.",
            NotificationTypeEnum.ERROR
          );
        }
      }
    },
    onError: () => {
      setNotification(
        "Something went wrong. Please try again.",
        NotificationTypeEnum.ERROR
      );
    },
  });

  const handleAddImagesClick = (itemId: number) => {
    const input = fileInputRefs.current[itemId];
    if (input) {
      input.click();
    }
  };

  const handleFileChange = async (
    itemId: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!orderId) return;

    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const success = await dataService.uploadReturnOrderItemImage(
        Number(orderId),
        itemId,
        file
      );

      if (success) {
        event.target.value = "";
      } else {
        alert("Failed to upload image.");
      }
    } catch (err) {
      console.error("Upload error", err);
      alert("An error occurred while uploading the image.");
    }
  };

  const handleQuickReturnSubmit = async (note: string, palletFlag: boolean) => {
    const payload: QuickReturnPayload = {
      orderId: Number(orderId),
      note,
      palletFlag,
    };

    const result = await dataService.sendQuickReturnRequest(payload);

    if (result) {
      setQuickReturnOpen(false);
      setThankYouOpen(true);
    } else {
      alert(result);
    }
  };

  return (
    <Box>
      <div id="ph-title" className="text-center page-title">
        <h1>Return Request</h1>
      </div>
      <Box sx={{ padding: "0px 20px", marginTop: "-30px" }}>
        <Typography
          onClick={() => navigate("/order/history")}
          sx={{
            color: "#54afe2",
            fontSize: "14px",
            fontFamily: 'normal 14px "Montserrat", Verdana, sans-serif',
            cursor: "pointer",
            textDecoration: "none",
          }}
        >
          {"< Back to All Orders"}
        </Typography>

        <Typography
          fontSize={"28px"}
          fontWeight={600}
          textAlign={"center"}
          color={"#494440"}
          fontFamily={'"Montserrat",Verdana,sans-serif'}
        >
          How Many Do You Need To Return?
        </Typography>

        <Grid container spacing={4} pb={2}>
          <Grid item xs={12} md={8}>
            <Typography
              sx={{
                fontFamily: '"Montserrat", Verdana, sans-serif',
                fontSize: "17.5px",
                fontWeight: "bold",
                mb: 2,
              }}
            >
              Items In Return
            </Typography>

            {items.map((item) => (
              <Paper
                key={item.id}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3} md={2} textAlign={"center"}>
                    <Box
                      component="img"
                      src={
                        item.pictureUrl
                          ? `${process.env.REACT_APP_CDN_URL}/images/general/picture_${item.pictureUrl}`
                          : defaultProductImg
                      }
                      alt={item.productName}
                      sx={{
                        width: "100%",
                        maxWidth: 180,
                        borderRadius: 1,
                        backgroundColor: item.colorHex || "transparent",
                        objectFit: "cover",
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={9} md={10}>
                    <Typography
                      sx={{
                        fontSize: "16.8px",
                        fontFamily: '"Montserrat", Verdana, sans-serif',
                        mb: 1,
                        color: "#494440",
                      }}
                    >
                      {item.productName}
                    </Typography>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", mb: 2 }}
                    >
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        QTY Being Returned
                      </Typography>

                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          select
                          size="small"
                          value={item.quantity}
                          onChange={(e) =>
                            handleQtyChange(item.id, Number(e.target.value))
                          }
                          sx={{ width: 80, mr: 2 }}
                        >
                          {Array.from(
                            { length: item.availableQuantity },
                            (_, idx) => idx + 1
                          ).map((qty) => (
                            <MenuItem key={qty} value={qty}>
                              {qty}
                            </MenuItem>
                          ))}
                        </TextField>

                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontFamily: '"Montserrat", Verdana, sans-serif',
                            mb: 1,
                            color: "#494440",
                          }}
                        >
                          Of {item.availableQuantity} Available to Return
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        ref={(el) => {
                          fileInputRefs.current[item.id] = el;
                        }}
                        onChange={(e) => handleFileChange(item.id, e)}
                      />

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleAddImagesClick(item.id)}
                      >
                        + Add Images
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontFamily: '"Montserrat", Verdana, sans-serif',
                          color: "#494440",
                          mr: 2,
                        }}
                      >
                        Was your order on a pallet?
                      </Typography>

                      <RadioGroup
                        row
                        value={itemPalletFlags[item.id] ?? ""}
                        onChange={async (e) => {
                          const value = e.target.value as "yes" | "no";
                          setItemPalletFlags((prev) => ({
                            ...prev,
                            [item.id]: value,
                          }));
                          await syncItemToServer(item.id, {
                            newPalletFlag: value,
                          });
                        }}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                          "& .MuiRadio-root": {
                            transform: "scale(0.8)",
                          },
                        }}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </Box>

                    <TextField
                      placeholder="Please provide more details or order notes"
                      multiline
                      minRows={4}
                      fullWidth
                      value={item.notes ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleItemNotesChange(item.id, value);
                      }}
                      onBlur={async () => {
                        const currentItem = items.find((i) => i.id === item.id);
                        const latestNote = currentItem?.notes ?? "";
                        await syncItemToServer(item.id, {
                          newNote: latestNote,
                        });
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography
              fontSize={"16px"}
              fontWeight={600}
              color={"#494440"}
              fontFamily={'"Montserrat",Verdana,sans-serif'}
              sx={{ mb: 2 }}
            >
              Return Summary
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography
              fontWeight={600}
              fontSize={"14px"}
              color={"#494440"}
              fontFamily={'"Montserrat",Verdana,sans-serif'}
              sx={{ mb: 1 }}
            >
              Requested pickup date
            </Typography>

            <Box sx={{ mt: 2, maxWidth: "260px" }}>
              <Typography
                fontSize={"14px"}
                color={"#494440"}
                fontFamily={'"Montserrat",Verdana,sans-serif'}
              >
                Pickup Date:
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  className="form-input"
                  value={pickupDate}
                  minDate={minPickupDate}
                  open={pickupDatePickerOpen}
                  shouldDisableDate={shouldDisablePickupDate}
                  onOpen={() => setPickupDatePickerOpen(true)}
                  onClose={() => setPickupDatePickerOpen(false)}
                  onChange={(newValue) => handlePickupDateChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                      {...params}
                      size="small"
                      onClick={() => setPickupDatePickerOpen(true)}
                    />
                  )}
                  renderDay={getRenderDayWithTooltip(holidays, "pickup")}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ mb: 3, mt: 2, maxWidth: "260px" }}>
              <Typography
                fontSize={"14px"}
                color={"#494440"}
                fontFamily={'"Montserrat",Verdana,sans-serif'}
              >
                Pickup Time :
              </Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={pickupTime}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                    fontFamily: '"Montserrat", Verdana, sans-serif',
                  },
                }}
                onChange={(e) => setPickupTime(e.target.value)}
              >
                {TimeOptions.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    sx={{
                      fontSize: "14px",
                      fontFamily: '"Montserrat", Verdana, sans-serif',
                    }}
                    disabled={disabledPickupTimes.has(opt.value)}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Typography
              fontSize={"14px"}
              color={"#494440"}
              fontFamily={'"Montserrat",Verdana,sans-serif'}
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Pickup Location
            </Typography>
            <Typography
              fontSize={"14px"}
              color={"#494440"}
              fontFamily={'"Montserrat",Verdana,sans-serif'}
              sx={{ mb: 1 }}
            >
              Is the pickup location the same as the delivery location?
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ mr: 1 }}
                fontSize={"14px"}
                color={"#494440"}
                fontFamily={'"Montserrat",Verdana,sans-serif'}
              >
                Yes
              </Typography>
              <Switch
                checked={!sameLocation}
                onChange={(e) => setSameLocation(!e.target.checked)}
              />
              <Typography
                fontSize={"14px"}
                color={"#494440"}
                fontFamily={'"Montserrat",Verdana,sans-serif'}
                sx={{ ml: 1 }}
              >
                No
              </Typography>
            </Box>

            {sameLocation ? (
              <Typography
                fontSize={"14px"}
                color={"#494440"}
                fontFamily={'"Montserrat",Verdana,sans-serif'}
                sx={{ whiteSpace: "pre-line" }}
              >
                {deliveryAddress}
              </Typography>
            ) : (
              <>
                {newShippingAddress && (
                  <Paper sx={{ p: 2, mt: 1 }}>
                    <AddressForm
                      address={newShippingAddress}
                      onChange={setNewShippingAddress}
                    />
                  </Paper>
                )}
              </>
            )}
            <Box sx={{ mb: 3, mt: 2, maxWidth: "260px" }}>
              <Typography
                fontSize={"14px"}
                color={"#494440"}
                fontFamily={'"Montserrat",Verdana,sans-serif'}
              >
                Job Name :
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
              />
            </Box>

            <Button
              onClick={() => handleSubmit.mutate()}
              className="blueButtonOutline filledBlueButton"
              sx={{ minWidth: "270px", mb: 2 }}
            >
              SUBMIT RETURN REQUEST
            </Button>

            <Typography
              fontSize={"14px"}
              fontFamily={'"Montserrat",Verdana,sans-serif'}
            >
              Need more assistance?
              <br />
              Contact{" "}
              <span
                style={{
                  color: "#54afe2",
                  cursor: "pointer",
                  textDecoration: "none",
                }}
                onClick={() => setQuickReturnOpen(true)}
              >
                Branch.
              </span>
            </Typography>
            <Typography
              maxWidth={"300px"}
              fontSize={"11px"}
              color={"#494440"}
              fontFamily={'"Montserrat",Verdana,sans-serif'}
              mt={2}
            >
              <strong>Please Note:</strong> <br />
              Returns may be subject to surcharges and fees. Special order
              products may not be returned. Return quantities will be verified,
              and return amounts will be calculated by your branch upon
              approval.
            </Typography>
          </Grid>
        </Grid>
        <QuickReturnDialog
          open={quickReturnOpen}
          onClose={() => setQuickReturnOpen(false)}
          onSubmit={handleQuickReturnSubmit}
        />
        <ThankYouDialog
          open={thankYouOpen}
          onClose={() => setThankYouOpen(false)}
        />
      </Box>

      {(handleSubmit.isPending || isPageLoading) && <PageLoadingOverlay />}
    </Box>
  );
};

export default ReturnItemsDetails;
